/**
 * Polish formatting for money and numbers. One place, so 52 000,00 zł looks the
 * same on every screen and nobody re-invents a toFixed(2) with a dot.
 */

const CURRENCY_LABEL: Record<string, string> = { PLN: 'zł', EUR: '€' }

/**
 * Money as Poles read it: space-grouped thousands, comma decimals, symbol last.
 * `null` renders as an em dash — a missing price is missing, not zero.
 */
// `useGrouping: 'always'` on purpose: pl-PL defaults to not grouping 4-digit
// numbers, which puts "6325,00 zł" next to "22 900,00 zł" in the same column
// and makes a price table impossible to scan down. Always group.
const MONEY_FMT = new Intl.NumberFormat('pl-PL', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  useGrouping: 'always',
})

export function money(amount: string | number | null | undefined, currency = 'PLN'): string {
  if (amount === null || amount === undefined || amount === '') return '—'
  const n = typeof amount === 'string' ? Number(amount) : amount
  if (!Number.isFinite(n)) return '—'
  return `${MONEY_FMT.format(n)} ${CURRENCY_LABEL[currency] ?? currency}`
}

/** Percent with up to two decimals, trailing zeros trimmed: 22 %, 4,5 %. */
export function percent(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = typeof value === 'string' ? Number(value) : value
  if (!Number.isFinite(n)) return '—'
  return `${n.toLocaleString('pl-PL', { maximumFractionDigits: 2 })} %`
}

const CATEGORY_LABEL: Record<string, string> = {
  opryskiwacz: 'Opryskiwacz',
  rozdrabniacz_galezi: 'Rozdrabniacz gałęzi',
  kosiarka: 'Kosiarka',
  platforma_sadownicza: 'Platforma sadownicza',
  przyczepa: 'Przyczepa',
  podnosnik: 'Podnośnik',
  sortownica: 'Sortownica',
  ciagnik_sadowniczy: 'Ciągnik sadowniczy',
  rozrzutnik: 'Rozrzutnik',
  rozsiewacz: 'Rozsiewacz nawozów',
  uprawa_gleby: 'Uprawa gleby i siew',
  osprzet_ladowacza: 'Osprzęt do ładowacza',
  zbior_owocow: 'Zbiór owoców',
  pielegnacja_sadu: 'Pielęgnacja sadu',
  inne: 'Inne',
}

export function categoryLabel(c: string): string {
  return CATEGORY_LABEL[c] ?? c
}

/** Polish plural: 1 maszyna, 2-4 maszyny, 5+ maszyn (with the teens exception). */
export function plural(n: number, one: string, few: string, many: string): string {
  if (n === 1) return one
  const t = n % 10
  const h = n % 100
  if (t >= 2 && t <= 4 && !(h >= 12 && h <= 14)) return few
  return many
}

/** "dziś", "wczoraj", "3 dni temu", then a plain date. For note timelines. */
export function relativeDay(iso: string): string {
  const d = new Date(iso)
  const today = new Date()
  const days = Math.round((startOfDay(today).getTime() - startOfDay(d).getTime()) / 86400_000)
  if (days === 0) return 'dziś'
  if (days === 1) return 'wczoraj'
  if (days < 7) return `${days} dni temu`
  return d.toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' })
}

export function dateTime(iso: string): string {
  return new Date(iso).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

export const CATEGORIES = Object.entries(CATEGORY_LABEL).map(([value, label]) => ({ value, label }))

// ── Egzemplarze (Etap 6) ─────────────────────────────────────────────────────

// Status egzemplarza to WYŁĄCZNIE logistyka (blueprint §4.3) — czy jest
// zarezerwowany/sprzedany wynika z danych, nie stąd.
const UNIT_STATUS_LABEL: Record<string, string> = {
  on_order: 'Zamówiony',
  in_transit: 'W transporcie',
  in_stock: 'Na placu',
  handed_over: 'Wydany',
}

export function unitStatusLabel(s: string): string {
  return UNIT_STATUS_LABEL[s] ?? s
}

/**
 * Wiek egzemplarza na placu w dniach — od przyjęcia. SPEC 5.4: powyżej 180 dni
 * na czerwono. Zwraca też etykietę, żeby lista i karta liczyły tak samo.
 */
export function ageOnLot(receivedAtIso: string | null): { days: number | null; label: string; overdue: boolean } {
  if (!receivedAtIso) return { days: null, label: '—', overdue: false }
  const received = new Date(receivedAtIso)
  const days = Math.max(0, Math.round((startOfDay(new Date()).getTime() - startOfDay(received).getTime()) / 86400_000))
  return { days, label: `${days} ${plural(days, 'dzień', 'dni', 'dni')}`, overdue: days > 180 }
}

// ── Serwis i zadania (Etap 7) ─────────────────────────────────────────────────

const SERVICE_STATUS_LABEL: Record<string, string> = {
  przyjete: 'Przyjęte',
  w_trakcie: 'W trakcie',
  oczekiwanie_na_czesci: 'Oczekiwanie na części',
  zakonczone: 'Zakończone',
}
export function serviceStatusLabel(s: string): string {
  return SERVICE_STATUS_LABEL[s] ?? s
}

const SERVICE_TYPE_LABEL: Record<string, string> = {
  gwarancyjny: 'Gwarancyjny',
  pogwarancyjny: 'Pogwarancyjny',
  przeglad: 'Przegląd',
}
export function serviceTypeLabel(s: string): string {
  return SERVICE_TYPE_LABEL[s] ?? s
}

const CLAIM_STATUS_LABEL: Record<string, string> = {
  brak: 'Bez reklamacji',
  zlozone: 'Reklamacja złożona',
  uznane: 'Reklamacja uznana',
  odrzucone: 'Reklamacja odrzucona',
}
export function claimStatusLabel(s: string): string {
  return CLAIM_STATUS_LABEL[s] ?? s
}

const TASK_TYPE_LABEL: Record<string, string> = {
  follow_up: 'Follow-up oferty',
  przeglad_gwarancyjny: 'Przegląd gwarancyjny',
  serwis: 'Serwis',
  reczne: 'Zadanie',
}
export function taskTypeLabel(s: string): string {
  return TASK_TYPE_LABEL[s] ?? s
}

/**
 * Status gwarancji egzemplarza (SPEC 5.5): duży wskaźnik „GWARANCJA DO …" albo
 * „PO GWARANCJI". Liczony na żywo z daty końca gwarancji — jedno miejsce, żeby
 * karta egzemplarza, serwis i karta klienta mówiły to samo.
 */
export function warrantyStatus(warrantyEndIso: string | null): { active: boolean; label: string } {
  if (!warrantyEndIso) return { active: false, label: 'BRAK GWARANCJI' }
  const end = new Date(warrantyEndIso + 'T23:59:59')
  if (end.getTime() >= Date.now()) {
    return { active: true, label: `GWARANCJA DO ${end.toLocaleDateString('pl-PL')}` }
  }
  return { active: false, label: 'PO GWARANCJI' }
}

/** "za 3 dni", "dziś", "wczoraj", "5 dni temu" — for task due dates. */
export function dueLabel(iso: string): { label: string; overdue: boolean; today: boolean } {
  const due = new Date(iso)
  const days = Math.round((startOfDay(due).getTime() - startOfDay(new Date()).getTime()) / 86400_000)
  if (days === 0) return { label: 'dziś', overdue: false, today: true }
  if (days === 1) return { label: 'jutro', overdue: false, today: false }
  if (days === -1) return { label: 'wczoraj', overdue: true, today: false }
  if (days < 0) return { label: `${-days} dni temu`, overdue: true, today: false }
  if (days < 14) return { label: `za ${days} ${plural(days, 'dzień', 'dni', 'dni')}`, overdue: false, today: false }
  return { label: due.toLocaleDateString('pl-PL'), overdue: false, today: false }
}

/**
 * The model an offer line stands for: a group option can pick one model out of
 * a family ("SOLIS N" → "SOLIS N 90"), and then that is what the client sees.
 */
export function modelNaOfercie(
  item: { model: string; catalogCode: string; options: { variantModel?: string | null; variantCode?: string | null }[] },
): { model: string; catalogCode: string } {
  const w = item.options.find((o) => o.variantModel?.trim())
  const k = item.options.find((o) => o.variantCode?.trim())
  return { model: w?.variantModel?.trim() || item.model, catalogCode: k?.variantCode?.trim() || item.catalogCode }
}

/** Offer status the way the screens say it — never the raw enum. */
export const QUOTE_STATUS_LABEL: Record<string, string> = {
  szkic: 'szkic',
  pending_approval: 'czeka na akceptację',
  wyslana: 'wysłana',
  zaakceptowana: 'zaakceptowana',
  odrzucona: 'odrzucona',
  wygasla: 'wygasła',
}
export const QUOTE_STATUS_STYLE: Record<string, string> = {
  szkic: 'bg-slate-100 text-slate-600',
  pending_approval: 'bg-amber-100 text-amber-800',
  wyslana: 'bg-blue-100 text-blue-700',
  zaakceptowana: 'bg-green-100 text-green-700',
  odrzucona: 'bg-red-100 text-red-700',
  wygasla: 'bg-slate-100 text-slate-400',
}
export function quoteStatusLabel(s: string): string {
  return QUOTE_STATUS_LABEL[s] ?? s
}
